import React, { useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { ViewportPortal } from "../shared/ViewportDialog";
import { CP_MODAL_OVERLAY_STYLE, CP_MODAL_PANEL_STYLE } from "../shared/crewpilotModalStyles";
import {
  CSV_EXPORT,
  availableCsvExports,
  buildAccountantCsv,
  downloadCsv,
} from "../../helpers/accountantCsvExport";
import "./AccountantCsvExport.css";

const LABELS = {
  [CSV_EXPORT.INVOICES]: { title: "Invoices", hint: "Number, dates, client, project, subtotal, tax, total, paid, balance" },
  [CSV_EXPORT.PAYMENTS]: { title: "Payments", hint: "Receipts and reversals recorded against invoices" },
  [CSV_EXPORT.TIME]: { title: "Time logs", hint: "Hours, rate and labour cost per entry" },
  [CSV_EXPORT.MATERIALS]: { title: "Materials used", hint: "Quantity, unit cost and total cost per project" },
};

/**
 * B2: "Export CSV" for accountants. Shows only the exports the signed-in user's
 * existing permissions allow; renders nothing when none are allowed.
 */
export function AccountantCsvExport({ buttonClassName = "nx-btn", kinds = null, buttonLabel = "Export CSV" }) {
  const { actor, orgPolicies } = usePermissions();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ tone: "", text: "" });

  // One shared dialog; an entry point may preselect its dataset(s). The
  // capability gate is always availableCsvExports, never the caller.
  const exportsAllowed = availableCsvExports(actor, orgPolicies).filter(
    (kind) => !kinds || kinds.includes(kind),
  );
  if (!exportsAllowed.length) return null;

  async function run(kind) {
    setBusy(kind);
    setMessage({ tone: "", text: "" });
    try {
      const { filename, csv, rowCount } = await buildAccountantCsv(kind, {
        actor,
        orgPolicies,
        range: { from: from || null, to: to || null },
      });
      downloadCsv(filename, csv);
      setMessage({
        tone: "ok",
        text: rowCount
          ? `${LABELS[kind].title}: ${rowCount} row${rowCount === 1 ? "" : "s"} exported (${filename}).`
          : `${LABELS[kind].title}: no records in this period. An empty file with headers was saved.`,
      });
    } catch (error) {
      setMessage({ tone: "error", text: error?.message || "Export failed. Try again." });
    } finally {
      setBusy("");
    }
  }

  return (
    <>
      <button type="button" className={buttonClassName} onClick={() => setOpen(true)} data-testid="accountant-csv-open">
        {buttonLabel}
      </button>
      {open ? (
        <ViewportPortal>
          <div style={CP_MODAL_OVERLAY_STYLE} role="presentation">
            <div style={{ ...CP_MODAL_PANEL_STYLE, maxWidth: "480px" }} role="dialog" aria-modal="true" aria-labelledby="acsv-title" className="acsv">
              <h3 id="acsv-title" style={{ marginTop: 0 }}>
                {exportsAllowed.length === 1 ? `Export ${LABELS[exportsAllowed[0]].title.toLowerCase()} (CSV)` : "Export for your accountant"}
              </h3>
              <p className="acsv__note">CSV files open in Excel, Google Sheets or Numbers. Amounts are in CAD.</p>
              <div className="acsv__range">
                <label>
                  <span>From</span>
                  <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label>
                  <span>To</span>
                  <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
                </label>
              </div>
              <p className="acsv__note">Leave both dates empty to export everything.</p>
              <div className="acsv__list">
                {exportsAllowed.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    className="acsv__item"
                    onClick={() => run(kind)}
                    disabled={Boolean(busy)}
                    data-testid={`accountant-csv-${kind}`}
                  >
                    <b>{busy === kind ? "Exporting…" : LABELS[kind].title}</b>
                    <span>{LABELS[kind].hint}</span>
                  </button>
                ))}
              </div>
              {message.text ? (
                <p className={`acsv__msg acsv__msg--${message.tone}`} role={message.tone === "error" ? "alert" : "status"}>
                  {message.text}
                </p>
              ) : null}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "14px" }}>
                <button type="button" className="nx-btn" onClick={() => setOpen(false)} disabled={Boolean(busy)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </ViewportPortal>
      ) : null}
    </>
  );
}
